import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import PageSEO from '@/components/base/PageSEO';
import VideoCard from '@/components/feature/VideoCard';
import { videos } from '@/mocks/videos';
import { useEffect } from 'react';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  const video = useMemo(() => videos.find((v) => v.id === id), [id]);

  const relatedVideos = useMemo(() => {
    if (!video) return [];
    return videos
      .filter((v) => v.id !== video.id && (v.categorySlug === video.categorySlug || v.tags.some((t) => video.tags.includes(t))))
      .slice(0, 4);
  }, [video]);

  if (!video) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200/70">
              <i className="ri-error-warning-line text-3xl text-foreground-600"></i>
            </div>
            <h1 className="font-heading text-xl text-foreground-100 mb-2">视频未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该视频可能已被删除或链接无效</p>
            <Link
              to="/"
              className="inline-flex items-center gap-1 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const formatViews = (num: number) => {
    if (num >= 10000) return (num / 10000).toFixed(1) + '万';
    return num.toString();
  };

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': video.title,
    'description': video.excerpt,
    'image': video.thumbnail,
    'datePublished': video.publishDate,
    'dateModified': video.publishDate,
    'author': { '@type': 'Organization', 'name': '黑料视频' },
    'publisher': { '@type': 'Organization', 'name': '黑料视频', 'url': 'https://heiliaoshipin.com' },
    'mainEntityOfPage': { '@type': 'WebPage', '@id': `https://heiliaoshipin.com/article/${video.id}` },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://heiliaoshipin.com/' },
      { '@type': 'ListItem', 'position': 2, 'name': video.category, 'item': `https://heiliaoshipin.com/category/${video.categorySlug}` },
      { '@type': 'ListItem', 'position': 3, 'name': video.title, 'item': `https://heiliaoshipin.com/article/${video.id}` },
    ],
  };

  return (
    <>
      <PageSEO
        title={`${video.title} - 黑料视频`}
        description={video.excerpt}
        canonicalUrl={`https://heiliaoshipin.com/article/${video.id}`}
        ogImage={video.thumbnail}
        ogType="article"
        jsonLd={{ ...articleJsonLd, ...breadcrumbLd }}
      />
      <Navbar />
      <main className="pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <Breadcrumb
            items={[
              { name: '首页', path: '/' },
              { name: video.category, path: `/category/${video.categorySlug}` },
              { name: video.title },
            ]}
          />

          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <Link
                to={`/category/${video.categorySlug}`}
                className="px-2.5 py-1 rounded bg-secondary-100/80 text-secondary-900 text-xs font-medium whitespace-nowrap hover:bg-secondary-200 transition-colors duration-200"
              >
                {video.category}
              </Link>
              {video.isHot && (
                <span className="px-2.5 py-1 rounded bg-primary-500/10 text-primary-500 text-xs font-medium whitespace-nowrap">
                  热门
                </span>
              )}
              {video.isNew && (
                <span className="px-2.5 py-1 rounded bg-accent-500/10 text-accent-500 text-xs font-medium whitespace-nowrap">
                  最新
                </span>
              )}
            </div>

            <h1 className="font-heading text-xl md:text-3xl text-foreground-100 mb-4 leading-snug">
              {video.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-600 mb-6">
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-calendar-line"></i>
                {video.publishDate}
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-eye-line"></i>
                {formatViews(video.views)} 次观看
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-chat-3-line"></i>
                {formatViews(video.comments)} 条评论
              </span>
            </div>
          </div>

          <div className="relative aspect-video rounded-lg overflow-hidden mb-8 bg-background-100">
            <img
              src={video.thumbnail}
              alt={video.title}
              title={`${video.title} - 黑料视频`}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50 cursor-pointer hover:bg-primary-600 transition-all duration-300 hover:scale-110">
                <i className="ri-play-fill text-2xl md:text-3xl ml-1"></i>
              </div>
            </div>
            <div className="absolute bottom-4 right-4 px-3 py-1 rounded bg-black/70 text-foreground-200 text-sm whitespace-nowrap">
              {video.duration}
            </div>
          </div>

          <div className="prose-custom mb-10">
            <div className="bg-background-100 border border-background-300/60 rounded-lg p-6 md:p-8">
              <h2 className="font-heading text-lg text-foreground-100 mb-4">视频详情</h2>
              <p className="text-sm text-foreground-400 leading-relaxed mb-4">
                {video.excerpt}
              </p>
              <p className="text-sm text-foreground-500 leading-relaxed">
                该视频记录了近期娱乐圈备受关注的真实事件。视频内容来源于网络公开渠道，本站仅做整理收录。我们持续关注并更新相关进展，为网友提供最新、最全面的黑料视频内容。
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-10">
            <span className="text-xs text-foreground-600 whitespace-nowrap">标签：</span>
            {video.tags.map((tag) => (
              <span
                key={tag}
                className="px-2.5 py-1 rounded-full text-xs bg-background-100 text-foreground-400 border border-background-300/60 cursor-pointer hover:text-primary-400 hover:border-primary-500/30 transition-colors duration-200 whitespace-nowrap"
              >
                #{tag}
              </span>
            ))}
          </div>

          <div className="border-t border-background-300/60 pt-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-links-line text-xl text-accent-500"></i>
                </div>
                <h2 className="font-heading text-lg text-foreground-100">相关推荐</h2>
              </div>
            </div>

            {relatedVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedVideos.map((v) => (
                  <VideoCard key={v.id} video={v} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-foreground-500 text-center py-8">暂无相关推荐</p>
            )}
          </div>
        </article>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}